import {
  type AngleUnit,
  type ExprNode,
  evaluate,
} from "@/lib/tool-kit/math/expression";

/**
 * Propagation of uncertainty for independent measurements, by first-order (linear)
 * approximation — the method of every introductory lab manual and of the GUM.
 *
 * For q = f(x₁, …, xₙ) with independent uncertainties δxᵢ:
 *
 *     δq = √( Σ (∂f/∂xᵢ · δxᵢ)² )
 *
 * Sums and differences, products and quotients, and powers are special cases with
 * closed forms, offered separately because those are the forms students are asked to
 * show. The general case differentiates numerically.
 */

export interface Measured {
  value: number;
  /** The standard (±) uncertainty, ≥ 0. */
  uncertainty: number;
}

export interface Contribution {
  /** ∂q/∂x for this input (±1 in a sum; implied by the rule elsewhere). */
  sensitivity: number;
  /** |∂q/∂x| · δx: what this input alone would add to δq. */
  term: number;
  /** term² / Σ term²: this input's share of the variance, 0–1. */
  share: number;
  /** δx / |x|, where the rule works in relative uncertainties. */
  relative: number | null;
}

export interface Propagation {
  value: number;
  uncertainty: number;
  /** δq / |q|; null when q = 0. */
  relative: number | null;
  contributions: Contribution[];
}

function withShares(parts: Omit<Contribution, "share">[]): {
  contributions: Contribution[];
  total: number;
} {
  const variance = parts.reduce((sum, p) => sum + p.term * p.term, 0);
  return {
    contributions: parts.map((p) => ({
      ...p,
      share: variance > 0 ? (p.term * p.term) / variance : 0,
    })),
    total: Math.sqrt(variance),
  };
}

function relativeOf(value: number, uncertainty: number): number | null {
  return value === 0 ? null : uncertainty / Math.abs(value);
}

/** q = ±a ± b ± …: absolute uncertainties add in quadrature. */
export function propagateSum(
  terms: readonly { sign: 1 | -1; measured: Measured }[]
): Propagation {
  const value = terms.reduce((sum, t) => sum + t.sign * t.measured.value, 0);
  const { contributions, total } = withShares(
    terms.map((t) => ({
      sensitivity: t.sign,
      term: t.measured.uncertainty,
      relative: null,
    }))
  );
  return {
    value,
    uncertainty: total,
    relative: relativeOf(value, total),
    contributions,
  };
}

/**
 * q = a × b ÷ c …: relative uncertainties add in quadrature. Null when a value is zero,
 * where a relative uncertainty is undefined (the formula mode still handles it).
 */
export function propagateProduct(
  factors: readonly { op: "multiply" | "divide"; measured: Measured }[]
): Propagation | null {
  if (factors.some((f) => f.measured.value === 0)) {
    return null;
  }
  const value = factors.reduce(
    (product, f) =>
      f.op === "multiply"
        ? product * f.measured.value
        : product / f.measured.value,
    1
  );
  const relatives = factors.map(
    (f) => f.measured.uncertainty / Math.abs(f.measured.value)
  );
  const relative = Math.sqrt(relatives.reduce((s, r) => s + r * r, 0));
  const { contributions } = withShares(
    factors.map((f, i) => ({
      sensitivity:
        f.op === "multiply"
          ? value / f.measured.value
          : -value / f.measured.value,
      term: Math.abs(value) * relatives[i],
      relative: relatives[i],
    }))
  );
  return {
    value,
    uncertainty: Math.abs(value) * relative,
    relative,
    contributions,
  };
}

/** q = xⁿ: the relative uncertainty is multiplied by |n|. Null outside the domain. */
export function propagatePower(
  measured: Measured,
  exponent: number
): Propagation | null {
  const value = measured.value ** exponent;
  if (measured.value === 0 || !Number.isFinite(value)) {
    return null;
  }
  const inputRelative = measured.uncertainty / Math.abs(measured.value);
  const relative = Math.abs(exponent) * inputRelative;
  const uncertainty = Math.abs(value) * relative;
  return {
    value,
    uncertainty,
    relative,
    contributions: [
      {
        sensitivity: exponent * measured.value ** (exponent - 1),
        term: uncertainty,
        share: 1,
        relative: inputRelative,
      },
    ],
  };
}

/* ------------------------------------------------------- general formula */

/** ∛ε: the step that balances truncation and rounding error in a central difference. */
const STEP = Math.cbrt(Number.EPSILON);
/** Second-order term above 10% of the first-order term: linearisation is suspect. */
const NONLINEAR_RATIO = 0.1;

/**
 * df/dx at x by central difference, with step h = ∛ε · max(|x|, δx). Falls back to a
 * one-sided difference when one side leaves the function's domain (√x at x = 0⁺).
 */
export function numericDerivative(
  f: (x: number) => number,
  x: number,
  scale: number
): number | null {
  const h = STEP * (Math.max(Math.abs(x), scale) || 1);
  const here = f(x);
  const ahead = f(x + h);
  const behind = f(x - h);
  if (Number.isFinite(ahead) && Number.isFinite(behind)) {
    return (ahead - behind) / (2 * h);
  }
  if (Number.isFinite(ahead) && Number.isFinite(here)) {
    return (ahead - here) / h;
  }
  if (Number.isFinite(behind) && Number.isFinite(here)) {
    return (here - behind) / h;
  }
  return null;
}

export interface FormulaVariable {
  name: string;
  measured: Measured;
}

export interface FormulaContribution extends Contribution {
  name: string;
  /** The curvature over ±δx is large next to the slope: first order may mislead. */
  nonlinear: boolean;
}

export interface FormulaPropagation extends Propagation {
  contributions: FormulaContribution[];
}

export type FormulaFailure =
  | { error: "not-finite" }
  | { error: "derivative"; variable: string };

function isNonlinear(
  f: (x: number) => number,
  measured: Measured,
  slope: number
): boolean {
  const { value: x, uncertainty: d } = measured;
  if (d === 0) {
    return false;
  }
  const up = f(x + d);
  const down = f(x - d);
  if (!(Number.isFinite(up) && Number.isFinite(down))) {
    return true;
  }
  const curvature = Math.abs(up + down - 2 * f(x)) / 2;
  const linear = Math.abs(slope * d);
  return linear === 0 ? curvature > 0 : curvature / linear > NONLINEAR_RATIO;
}

/** Propagate through any formula the parser accepts. */
export function propagateFormula(
  ast: ExprNode,
  variables: readonly FormulaVariable[],
  unit: AngleUnit = "radians"
): FormulaPropagation | FormulaFailure {
  const env: Record<string, number> = {};
  for (const v of variables) {
    env[v.name] = v.measured.value;
  }
  const value = evaluate(ast, env, unit);
  if (!Number.isFinite(value)) {
    return { error: "not-finite" };
  }
  const parts: Omit<FormulaContribution, "share">[] = [];
  for (const v of variables) {
    const along = (x: number) => evaluate(ast, { ...env, [v.name]: x }, unit);
    const slope = numericDerivative(
      along,
      v.measured.value,
      v.measured.uncertainty
    );
    if (slope === null) {
      return { error: "derivative", variable: v.name };
    }
    parts.push({
      name: v.name,
      sensitivity: slope,
      term: Math.abs(slope) * v.measured.uncertainty,
      relative: relativeOf(v.measured.value, v.measured.uncertainty),
      nonlinear: isNonlinear(along, v.measured, slope),
    });
  }
  const { contributions, total } = withShares(parts);
  return {
    value,
    uncertainty: total,
    relative: relativeOf(value, total),
    contributions: contributions as FormulaContribution[],
  };
}

/* -------------------------------------------------------------- rounding */

export type UncertaintyRounding = "one-or-two" | "two";

/** Round half away from zero at 10^place, so −2.25 → −2.3 just as 2.25 → 2.3. */
function roundToPlace(x: number, place: number): number {
  const scaled = Math.round(Math.abs(x) / 10 ** place);
  // Dividing by a negative power is exact where multiplying by 10^place is not.
  const magnitude = place < 0 ? scaled / 10 ** -place : scaled * 10 ** place;
  return Math.sign(x) * magnitude;
}

export interface RoundedMeasurement {
  value: number;
  uncertainty: number;
  /** Decimal places both are written to. */
  decimals: number;
  /** Power of ten of the last digit kept. */
  place: number;
}

/**
 * Round a result the way a lab report writes it: the uncertainty to one significant
 * figure (two when it starts with a 1 — the usual rule, e.g. Taylor's *Introduction to
 * Error Analysis*), or always two; then the value to the same decimal place.
 * Null for a zero or non-finite uncertainty.
 */
export function roundMeasurement(
  value: number,
  uncertainty: number,
  rule: UncertaintyRounding
): RoundedMeasurement | null {
  if (
    !(uncertainty > 0 && Number.isFinite(uncertainty) && Number.isFinite(value))
  ) {
    return null;
  }
  let exponent = Math.floor(Math.log10(uncertainty));
  if (uncertainty / 10 ** exponent >= 10) {
    exponent += 1;
  }
  const leading = Math.floor(uncertainty / 10 ** exponent + 1e-9);
  const digits = rule === "two" || leading === 1 ? 2 : 1;
  let place = exponent - digits + 1;
  // 0.096 to one figure is 0.1, not "0.10": a carry moves the last place up.
  if (Math.round(uncertainty / 10 ** place) >= 10 ** digits) {
    place += 1;
  }
  return {
    value: roundToPlace(value, place),
    uncertainty: roundToPlace(uncertainty, place),
    decimals: Math.max(0, -place),
    place,
  };
}
